import { FC, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CAMPAIGN_ID } from '@constants/common';
import { useCampaignSources } from '@hooks/api/useCampaignSources';
import { useGetSearchParam } from '@hooks/common/useGetSearchParams';
import { useLeadScraperContext } from '@pages/LeadScraper/context';
import { compareCampaignType } from '@pages/LeadScraper/utils';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Step, Stepper, Typography } from '@mui/material';
import StepperStepIcon from '@components/StepperStepIcon/StepperStepIcon';

import BasicInformation from './components/BasicInformation';
import ScraperFilters from './components/ScraperFilters';
import ScraperScheduling from './components/ScraperScheduling';
import SourceSettings from './components/SourceSettings';
import CampaignTypeSelection from '../../../CampaignTypeStep';
import EmailConfigStep from '../../../EmailConfigStep';
import LeadImportStep from '../../../LeadImportStep';
// Visual Builder removed - can be separate advanced feature
import { StyledLink, StyledStepConnector, StyledStepContent, StyledStepLabel } from './styled';
import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

// Define all possible steps
const ALL_STEPS = {
  campaignType: { id: 'campaignType', Component: CampaignTypeSelection },
  basicInfo: { id: 'basicInfo', Component: BasicInformation },
  leadImport: { id: 'leadImport', Component: LeadImportStep },
  dataSource: { id: 'dataSource', Component: SourceSettings }, // Renamed from sourceSettings
  scraperConfig: { id: 'scraperConfig', Component: ScraperFilters }, // More descriptive name
  emailConfig: { id: 'emailConfig', Component: EmailConfigStep },
  scheduling: { id: 'scheduling', Component: ScraperScheduling },
};

// Define step configurations for each campaign type - Visual Builder removed
const STEP_CONFIGS = {
  'scraping-only': [
    'campaignType',
    'basicInfo',
    'dataSource',
    'scraperConfig',
    'scheduling',
  ],
  'scraping-email': [
    'campaignType',
    'basicInfo',
    'dataSource',
    'scraperConfig',
    'emailConfig',
    'scheduling',
  ],
  'email-only': [
    'campaignType',
    'basicInfo',
    'leadImport',
    'emailConfig',
    'scheduling',
  ],
  '': [ // Default when no campaign type selected
    'campaignType',
    'basicInfo',
    'dataSource',
    'scraperConfig',
    'scheduling',
  ],
};

const ScraperFlow: FC = () => {
  const { t } = useTranslation();
  const {
    activeStep,
    handleNext,
    handleBack,
    completedSteps,
    filtersForm,
    sourceSettingsForm,
    scraperSubtitleLocale,
    backLink,
    breadCrumbsLocale,
    campaignTypeForm,
  } = useLeadScraperContext();
  const campaignId: string | null = useGetSearchParam(CAMPAIGN_ID);
  const { data: rawSourcesData } = useCampaignSources(campaignId as string);
  // Ensure sourcesData is always an array
  const sourcesData = Array.isArray(rawSourcesData) ? rawSourcesData : [];
  const { autocomplete } = sourceSettingsForm.watch();
  const { campaignType } = campaignTypeForm.watch();
  const { isLargerScreen } = useScreenSizeContext();

  // Get the appropriate steps based on campaign type
  const steps = useMemo(() => {
    const stepConfig = STEP_CONFIGS[campaignType as keyof typeof STEP_CONFIGS] || STEP_CONFIGS[''];
    return stepConfig.map((stepId, index) => ({
      ...ALL_STEPS[stepId as keyof typeof ALL_STEPS],
      stepNumber: index + 1,
    }));
  }, [campaignType]);

  useEffect(() => {
    if (campaignId && autocomplete?.length > 0) {
      const isEqual = compareCampaignType(sourcesData, autocomplete);

      if (!isEqual) {
        sourceSettingsForm.reset();
        filtersForm.reset();
      }
    }
  }, [autocomplete, filtersForm, campaignId, sourceSettingsForm, sourcesData]);

  return (
    <Box ml="70px">
      <Box display="flex" mb={3} ml="-70px">
        <StyledLink variant="body2" mr={1} color="text.secondary" fontFamily="Inter" to={backLink} component={Link}>
          {t(breadCrumbsLocale)}
        </StyledLink>
        <ChevronRightRoundedIcon fontSize="small" />
        <Typography variant="body2" ml={1} color="text.primary" fontFamily="Inter">
          {t(scraperSubtitleLocale)}
        </Typography>
      </Box>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        connector={isLargerScreen ? <StyledStepConnector /> : null}
      >
        {steps.map((step, index) => {
          const { Component, stepNumber } = step;
          return (
            <Step key={step.id} expanded>
              <StyledStepLabel
                StepIconComponent={StepperStepIcon}
                isActive={activeStep >= index}
                isMobile={!isLargerScreen}
              >{`${t('dashboard.newScraper.step').toUpperCase()} ${stepNumber}`}</StyledStepLabel>
              <StyledStepContent
                isActive={activeStep > index}
                isLast={steps.length - 1 === index}
                isMobile={!isLargerScreen}
              >
                <Component
                  isActive={activeStep === index}
                  isCompleted={completedSteps.includes(index)}
                  handleNext={handleNext}
                  handleBack={() => handleBack(activeStep - index)}
                  campaignId={campaignId as string}
                />
              </StyledStepContent>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default memo(ScraperFlow);
